import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, DollarSign, Activity, AlertCircle,
  BarChart3, BrainCircuit, Shield, FileText, Settings,
  Clock, ExternalLink, ChevronRight, AlertTriangle,
  Newspaper, Bot as BotIcon, RefreshCw, CheckCircle,
  XCircle, Info, ChevronDown, Download, Filter, Zap, Eye,
  Loader2
} from 'lucide-react';
import { 
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { Bot, BotStatus, AutoConfigValues, LLMDecision } from '../types/api';
import { botAPI } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Checkbox } from './ui/checkbox';
import { Skeleton } from './ui/skeleton';
import { cn, formatCurrency, formatPercentage, formatNumber } from '../lib/utils';
import {
  RiskManagementCard,
  PositionMonitoringCard,
  TradingDecisionCard,
} from './decisions';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getApiEndpoint } from '../config/api';

// Type definitions remain the same...
interface NewsArticle {
  id: string;
  title: string;
  url: string;
  original_url: string;
  cryptopanic_url: string;
  resolved_url: string;
  created_at: string;
  domain: string;
  kind: string;
  source: Record<string, any>;
  currencies: Array<{
    code: string;
    title: string;
    slug: string;
    url: string;
  }>;
  votes: Record<string, any>;
  is_related: boolean;
}


interface LogEntry {
  timestamp: string;
  level: string;
  logger_name?: string;
  message: string;
  module?: string;
  function?: string;
  line?: number;
  exception?: string;
  data?: Record<string, any>;
}

interface LogData {
  recent: LogEntry[];
  errors: LogEntry[];
  warnings: LogEntry[];
  stats: {
    total_logs: number;
    error_count: number;
    warning_count: number;
    info_count: number;
    debug_count: number;
  };
}

// LLMDecision interface is now imported from '../types/api'

interface RiskAnalysis {
  timestamp: string;
  excluded_pairs: string[];
  risk_factors: string[];
  market_conditions: string;
  valid_opportunities_count: number;
  detailed_exclusions?: {
    risk_management?: Record<string, string[]>;
    trend_analysis?: Record<string, string[]>;
    volume_analysis?: Record<string, string[]>;
  };
}




interface BotDetailedInfo {
  bot: Bot;
  runtime_status: BotStatus;
  logs: LogData | LogEntry[];  // Support both old and new format
  last_llm_decision?: LLMDecision;
  risk_analysis?: RiskAnalysis;
  llm_decisions?: LLMDecision[];
  current_auto_config?: AutoConfigValues;
  log_error?: string;  // Error message if logs couldn't be retrieved
}

interface BotDetailModalProps {
  bot: Bot;
  onClose: () => void;
}

const BotDetailModal: React.FC<BotDetailModalProps> = ({ bot, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [decisionTypeFilter, setDecisionTypeFilter] = useState<string>('all');

  // Store previous logs in a ref to preserve them when API returns null
  const previousLogsRef = React.useRef<LogData | LogEntry[] | undefined>(undefined);
  
  // Fetch detailed bot info
  const { data: detailedInfo, isLoading } = useQuery({
    queryKey: ['botDetail', bot.id],
    queryFn: async () => {
      const response = await botAPI.getBotStatus(bot.id);

      // Handle the case where logs might be null (lock couldn't be acquired)
      // In this case, keep the existing logs from the previous query
      let logsData: LogData | LogEntry[] | undefined = response.detailed_info?.logs as any;
      if (logsData === null && previousLogsRef.current) {
        // Keep existing logs if new response has null logs
        logsData = previousLogsRef.current;
      } else if (!logsData) {
        // Default empty structure if no logs at all
        logsData = {
          recent: [],
          errors: [],
          warnings: [],
          stats: {
            total_logs: 0,
            error_count: 0,
            warning_count: 0,
            info_count: 0,
            debug_count: 0
          }
        } as LogData;
      }

      // Update the ref for next time
      if (logsData && logsData !== null) {
        previousLogsRef.current = logsData;
      }

      return {
        bot: response.bot || bot,
        runtime_status: response.runtime_status,
        logs: logsData,
        last_llm_decision: response.detailed_info?.last_llm_decision,
        risk_analysis: response.detailed_info?.risk_analysis,
        llm_decisions: response.llm_decisions || [],
        current_auto_config: response.detailed_info?.current_auto_config,
        log_error: response.detailed_info?.log_error
      } as BotDetailedInfo;
    },
    refetchInterval: 5000, // Poll every 5 seconds
    staleTime: 0, // Always consider data stale to ensure refetchInterval works
  });

  // Fetch technical indicators when tab is active
  const { data: technicalIndicators, isLoading: indicatorsLoading, refetch: refetchIndicators } = useQuery({
    queryKey: ['technicalIndicators', bot.id],
    queryFn: () => botAPI.getTechnicalIndicators(bot.id),
    enabled: activeTab === 'indicators',
    refetchInterval: activeTab === 'indicators' ? 5000 : false,
  });

  // Fetch news when tab is active
  const { data: newsData, isLoading: newsLoading, refetch: refetchNews } = useQuery({
    queryKey: ['botNews', bot.id],
    queryFn: () => botAPI.getBotNews(bot.id),
    enabled: activeTab === 'news',
  });

  // Paginated AI Decisions
  const {
    data: decisionsData,
    fetchNextPage: fetchNextDecisions,
    hasNextPage: hasMoreDecisions,
    isFetchingNextPage: isFetchingMoreDecisions,
    isLoading: decisionsLoading,
  } = useInfiniteQuery({
    queryKey: ['botDecisions', bot.id, decisionTypeFilter],
    queryFn: async ({ pageParam = 0 }) => {
      return botAPI.getDecisions(bot.id, {
        decision_type: decisionTypeFilter === 'all' ? undefined : decisionTypeFilter,
        limit: 20,
        offset: pageParam,
      });
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.has_more) {
        return lastPage.offset + lastPage.limit;
      }
      return undefined;
    },
    initialPageParam: 0,
    enabled: activeTab === 'llm',
  });

  // Paginated Logs
  const [logLevelFilter, setLogLevelFilter] = useState<string>('all');
  const [isDownloadingLogs, setIsDownloadingLogs] = useState(false);

  // Handle log download
  const handleDownloadLogs = async () => {
    try {
      setIsDownloadingLogs(true);
      const blob = await botAPI.downloadLogs(bot.id, {
        level: logLevelFilter === 'all' ? undefined : logLevelFilter,
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      const levelSuffix = logLevelFilter === 'all' ? '' : `_${logLevelFilter.toLowerCase()}`;
      a.download = `bot_${bot.id}_${bot.name.replace(/\s+/g, '_')}_logs${levelSuffix}_${timestamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download logs:', error);
    } finally {
      setIsDownloadingLogs(false);
    }
  };

  const {
    data: logsData,
    fetchNextPage: fetchNextLogs,
    hasNextPage: hasMoreLogs,
    isFetchingNextPage: isFetchingMoreLogs,
    isLoading: logsLoading,
    refetch: refetchLogs,
  } = useInfiniteQuery({
    queryKey: ['botLogs', bot.id, logLevelFilter],
    queryFn: async ({ pageParam = 0 }) => {
      return botAPI.getLogs(bot.id, {
        level: logLevelFilter === 'all' ? undefined : logLevelFilter,
        limit: 50,
        offset: pageParam,
      });
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.has_more) {
        return lastPage.offset + lastPage.limit;
      }
      return undefined;
    },
    initialPageParam: 0,
    enabled: activeTab === 'logs',
  });

  // Flatten paginated data
  const allDecisions = useMemo(() => {
    return decisionsData?.pages.flatMap(page => page.decisions) || [];
  }, [decisionsData]);

  const decisionStats = useMemo(() => {
    return decisionsData?.pages[0]?.stats || { total: 0, initial: 0, position_monitoring: 0, risk_management: 0 };
  }, [decisionsData]);

  const allLogs = useMemo(() => {
    return logsData?.pages.flatMap(page => page.logs) || [];
  }, [logsData]);

  const logStats = useMemo(() => {
    return logsData?.pages[0]?.stats || { total_logs: 0, error_count: 0, warning_count: 0, info_count: 0, debug_count: 0 };
  }, [logsData]);

  const totalLogs = logsData?.pages[0]?.total || 0;
  const totalDecisions = decisionsData?.pages[0]?.total || 0;

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    return `${minutes}m ${secs}s`;
  };

  const getValidUrl = (article: NewsArticle) => {
    const isSecureUrl = (url: string) => {
      if (!url) return false;
      // eslint-disable-next-line no-script-url
      const dangerous_protocols = ['javascript:', 'data:', 'file:', 'ftp:', 'mailto:'];
      return (url.startsWith('http://') || url.startsWith('https://')) &&
             !dangerous_protocols.some(proto => url.toLowerCase().startsWith(proto));
    };

    if (article.resolved_url && isSecureUrl(article.resolved_url)) return article.resolved_url;
    if (article.url && isSecureUrl(article.url)) return article.url;
    if (article.original_url && isSecureUrl(article.original_url)) return article.original_url;
    if (article.source?.url && isSecureUrl(article.source.url)) return article.source.url;
    if (article.domain) return `https://${article.domain.replace(/[^a-zA-Z0-9.-]/g, '')}`;
    return '#';
  };

  // Prepare chart data
  const positionChartData = detailedInfo?.runtime_status.current_positions.map(pos => ({
    symbol: pos.symbol,
    value: pos.current_value,
    pnl: pos.profit_loss,
    pnlPct: pos.profit_loss_pct,
  })) || [];

  const performanceData = detailedInfo?.runtime_status.recent_trades.slice(0, 10).reverse().map((trade, index) => ({
    index: index + 1,
    profit: trade.profit_loss || 0,
    symbol: trade.symbol,
  })) || [];

  // Determine which tabs should be visible based on bot configuration
  const getVisibleTabs = () => {
    const tabs = [
      { id: 'overview', label: 'Overview', icon: BarChart3, alwaysVisible: true },
      { id: 'trading', label: 'Trading', icon: TrendingUp, alwaysVisible: true },
      { id: 'indicators', label: 'Indicators', icon: Activity, alwaysVisible: true },
      { id: 'news', label: 'News', icon: Newspaper, alwaysVisible: false, condition: bot.config.news_fetcher },
      { 
        id: 'llm', 
        label: 'AI Decisions', 
        icon: BrainCircuit, 
        alwaysVisible: true,
        statusIndicator: bot.config.llm_no_action ? 'readonly' : 'active'
      },
      { id: 'risk', label: 'Risk', icon: Shield, alwaysVisible: true },
      { id: 'logs', label: 'Logs', icon: FileText, alwaysVisible: true },
      { id: 'config', label: 'Config', icon: Settings, alwaysVisible: true }
    ];
    
    return tabs.filter(tab => tab.alwaysVisible || tab.condition);
  };

  const visibleTabs = getVisibleTabs();
  
  // Handle dynamic grid columns based on tab count
  const getGridColsClass = (count: number) => {
    const gridClasses = {
      1: 'grid-cols-1',
      2: 'grid-cols-2', 
      3: 'grid-cols-3',
      4: 'grid-cols-4',
      5: 'grid-cols-5',
      6: 'grid-cols-6',
      7: 'grid-cols-7',
      8: 'grid-cols-8',
    };
    return gridClasses[count as keyof typeof gridClasses] || 'grid-cols-8';
  };
  
  const gridCols = getGridColsClass(visibleTabs.length);

  // Ensure activeTab is valid for visible tabs
  const validActiveTab = visibleTabs.find(tab => tab.id === activeTab) ? activeTab : 'overview';

  // Fix: Move setState to useEffect to avoid setting state during render
  React.useEffect(() => {
    if (validActiveTab !== activeTab) {
      setActiveTab(validActiveTab);
    }
  }, [validActiveTab, activeTab]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl h-[90vh] sm:h-[90vh] h-[95vh] flex flex-col p-0 mx-2 sm:mx-auto">
        {/* Fixed Header */}
        <DialogHeader className="px-3 sm:px-6 py-3 sm:py-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "p-2 rounded-lg",
                detailedInfo?.runtime_status.is_running ? "bg-green-500/10" : "bg-gray-500/10"
              )}>
                <BotIcon className={cn(
                  "h-5 w-5",
                  detailedInfo?.runtime_status.is_running ? "text-green-500" : "text-gray-500"
                )} />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">{bot.name}</DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  {detailedInfo && (
                    <Badge variant={detailedInfo.runtime_status.is_running ? "success" : "secondary"}>
                      {detailedInfo.runtime_status.is_running ? 'Running' : 'Stopped'}
                    </Badge>
                  )}
                  {bot.config.testnet && (
                    <Badge variant="warning">Testnet</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Fixed Tabs Container */}
        <div className="flex-1 flex flex-col min-h-0">
          <Tabs value={validActiveTab} onValueChange={setActiveTab} className="flex flex-col h-full">
            {/* Fixed Tab List */}
            <div className="px-3 sm:px-6 pt-4 pb-0 flex-shrink-0">
              {/* Mobile: Dropdown Navigation */}
              <div className="block sm:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full justify-between h-10">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const activeTab = visibleTabs.find(tab => tab.id === validActiveTab);
                          if (!activeTab) return null;
                          const IconComponent = activeTab.icon;
                          return (
                            <>
                              <IconComponent className="h-4 w-4" />
                              <span className="font-medium">{activeTab.label}</span>
                              {activeTab.statusIndicator === 'readonly' && (
                                <Badge variant="secondary" className="text-xs py-0 px-1">
                                  View Only
                                </Badge>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[calc(100vw-1.5rem)] max-w-sm">
                    {visibleTabs.map((tab) => {
                      const IconComponent = tab.icon;
                      return (
                        <DropdownMenuItem
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={cn(
                            "flex items-center gap-2 cursor-pointer",
                            validActiveTab === tab.id && "bg-accent"
                          )}
                        >
                          <IconComponent className="h-4 w-4" />
                          <span>{tab.label}</span>
                          {tab.statusIndicator === 'readonly' && (
                            <Badge variant="secondary" className="ml-auto text-xs py-0 px-1">
                              View Only
                            </Badge>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {/* Desktop: Grid layout */}
              <div className="hidden sm:block">
                <TabsList className={cn("grid w-full", gridCols)}>
                  {visibleTabs.map((tab) => {
                    const IconComponent = tab.icon;
                    return (
                      <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
                        <IconComponent className="h-4 w-4" />
                        {tab.label}
                        {tab.statusIndicator === 'readonly' && (
                          <Badge variant="secondary" className="ml-1 text-xs px-1 py-0">
                            View Only
                          </Badge>
                        )}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-hidden">
              <div className="h-full overflow-y-auto px-3 sm:px-6 py-3 sm:py-4">
                {isLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-32" />
                    <Skeleton className="h-48" />
                    <Skeleton className="h-32" />
                  </div>
                ) : (
                  <>
                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-6 data-[state=inactive]:hidden">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                      >
                        {/* Stats Cards */}
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                              Trading Capital
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-baseline gap-2">
                              <span className="text-2xl font-bold">
                                {formatCurrency(bot.config.trading_capital)}
                              </span>
                              <DollarSign className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                              Uptime
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-baseline gap-2">
                              <span className="text-2xl font-bold">
                                {detailedInfo?.runtime_status.uptime 
                                  ? formatUptime(detailedInfo.runtime_status.uptime)
                                  : '0m'}
                              </span>
                              <Clock className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                              Open Positions
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-baseline gap-2">
                              <span className="text-2xl font-bold">
                                {detailedInfo?.runtime_status.current_positions.length || 0}
                              </span>
                              <Activity className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                              Recent Trades
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-baseline gap-2">
                              <span className="text-2xl font-bold">
                                {detailedInfo?.runtime_status.recent_trades.length || 0}
                              </span>
                              <TrendingUp className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>

                      {/* Charts */}
                      {positionChartData.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                        >
                          <Card>
                            <CardHeader>
                              <CardTitle>Portfolio Distribution</CardTitle>
                              <CardDescription>Current position values</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                  <Pie
                                    data={positionChartData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={(entry: any) => `${entry.symbol}: $${(entry.value ?? 0).toFixed(2)}`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                  >
                                    {positionChartData.map((entry, index) => (
                                      <Cell 
                                        key={`cell-${index}`} 
                                        fill={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][index % 5]} 
                                      />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                                  <Legend />
                                </PieChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader>
                              <CardTitle>Position Performance</CardTitle>
                              <CardDescription>Profit/Loss by position</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={positionChartData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="symbol" />
                                  <YAxis />
                                  <Tooltip 
                                    formatter={(value: number) => formatCurrency(value)}
                                    labelFormatter={(label) => `Symbol: ${label}`}
                                  />
                                  <Bar dataKey="pnl" fill="#3b82f6">
                                    {positionChartData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? '#10b981' : '#ef4444'} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        </motion.div>
                      )}

                      {/* Error State */}
                      {detailedInfo?.runtime_status.error_message && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="bg-destructive/10 border border-destructive/20 rounded-lg p-4"
                        >
                          <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                            <div>
                              <h4 className="font-medium text-destructive">Bot Error</h4>
                              <p className="text-sm text-destructive/80 mt-1">
                                {detailedInfo.runtime_status.error_message}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </TabsContent>

                    {/* Trading Tab */}
                    <TabsContent value="trading" className="space-y-6 data-[state=inactive]:hidden">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                      >
                        {/* Current Positions */}
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle className="flex items-center gap-2">
                                  <TrendingUp className="h-5 w-5" />
                                  Current Positions
                                </CardTitle>
                                <CardDescription>
                                  {detailedInfo?.runtime_status.current_positions.length || 0} active positions
                                </CardDescription>
                              </div>
                              {detailedInfo?.runtime_status.current_positions.length ? (
                                <Badge variant="outline" className="text-xs">
                                  Total: {formatCurrency(
                                    detailedInfo.runtime_status.current_positions.reduce(
                                      (sum, pos) => sum + pos.current_value, 0
                                    )
                                  )}
                                </Badge>
                              ) : null}
                            </div>
                          </CardHeader>
                          <CardContent>
                            {detailedInfo?.runtime_status.current_positions.length ? (
                              <div className="space-y-3">
                                {detailedInfo.runtime_status.current_positions.map((position, index) => (
                                  <div
                                    key={index}
                                    className={cn(
                                      "flex items-center justify-between p-4 rounded-lg border",
                                      position.profit_loss >= 0
                                        ? "bg-green-50/50 dark:bg-green-950/10 border-green-200/50 dark:border-green-800/30"
                                        : "bg-red-50/50 dark:bg-red-950/10 border-red-200/50 dark:border-red-800/30"
                                    )}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={cn(
                                        "p-2 rounded-lg",
                                        position.profit_loss >= 0 ? "bg-green-100 dark:bg-green-900/30" : "bg-red-100 dark:bg-red-900/30"
                                      )}>
                                        <TrendingUp className={cn(
                                          "h-4 w-4",
                                          position.profit_loss >= 0 ? "text-green-600" : "text-red-600"
                                        )} />
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <p className="font-semibold">{position.symbol}</p>
                                          {position.is_managed === false && (
                                            <Badge variant="outline" className="text-[10px] px-1 py-0">
                                              Synced
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                          {position.quantity} @ {formatCurrency(position.entry_price)}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-medium">{formatCurrency(position.current_value)}</p>
                                      <p className={cn(
                                        "text-sm font-bold flex items-center justify-end gap-1",
                                        position.profit_loss >= 0 ? "text-green-600" : "text-red-600"
                                      )}>
                                        <span className="text-xs font-normal opacity-70">
                                          {position.profit_loss >= 0 ? 'Profit' : 'Loss'}
                                        </span>
                                        {formatCurrency(position.profit_loss)} ({formatPercentage(position.profit_loss_pct)})
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="p-3 bg-muted rounded-full mb-4">
                                  <TrendingUp className="h-8 w-8 text-muted-foreground" />
                                </div>
                                <p className="font-medium text-muted-foreground">No Open Positions</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  When the bot opens trades, they will appear here
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        {/* Trade History Chart */}
                        {performanceData.length > 0 && (
                          <Card>
                            <CardHeader>
                              <CardTitle>Recent Trade Performance</CardTitle>
                              <CardDescription>Last 10 trades P&L</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={performanceData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="index" label={{ value: 'Trade #', position: 'insideBottom', offset: -5 }} />
                                  <YAxis label={{ value: 'Profit/Loss ($)', angle: -90, position: 'insideLeft' }} />
                                  <Tooltip 
                                    formatter={(value: number) => formatCurrency(value)}
                                    labelFormatter={(label) => `Trade #${label}`}
                                  />
                                  <Area 
                                    type="monotone" 
                                    dataKey="profit" 
                                    stroke="#3b82f6"
                                    fill="#3b82f6"
                                    fillOpacity={0.3}
                                  />
                                </AreaChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        )}

                        {/* Recent Trades */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Clock className="h-5 w-5" />
                              Recent Trades
                            </CardTitle>
                            <CardDescription>
                              Last {Math.min(5, detailedInfo?.runtime_status.recent_trades.length || 0)} trades
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            {detailedInfo?.runtime_status.recent_trades.length ? (
                              <div className="space-y-3">
                                {detailedInfo.runtime_status.recent_trades.slice(0, 5).map((trade, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <Badge variant={trade.action === 'BUY' ? 'success' : 'destructive'}>
                                        {trade.action}
                                      </Badge>
                                      <div>
                                        <p className="font-medium">{trade.symbol}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {new Date(trade.timestamp).toLocaleString()}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-medium">
                                        {trade.quantity} @ {formatCurrency(trade.price)}
                                      </p>
                                      {trade.profit_loss !== undefined && (
                                        <p className={cn(
                                          "text-sm font-bold flex items-center justify-end gap-1",
                                          trade.profit_loss >= 0 ? "text-green-600" : "text-red-600"
                                        )}>
                                          <span className="text-xs font-normal opacity-70">
                                            {trade.profit_loss >= 0 ? 'Profit' : 'Loss'}
                                          </span>
                                          {formatCurrency(trade.profit_loss)}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="p-3 bg-muted rounded-full mb-4">
                                  <Clock className="h-8 w-8 text-muted-foreground" />
                                </div>
                                <p className="font-medium text-muted-foreground">No Trade History</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  Completed trades will appear here
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    </TabsContent>

                    {/* Technical Indicators Tab */}
                    <TabsContent value="indicators" className="space-y-6 data-[state=inactive]:hidden">
                      {indicatorsLoading ? (
                        <div className="space-y-4">
                          <Skeleton className="h-32" />
                          <Skeleton className="h-64" />
                        </div>
                      ) : technicalIndicators ? (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          {/* Summary Card */}
                          <Card>
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <div>
                                  <CardTitle>Technical Analysis Summary</CardTitle>
                                  <CardDescription>
                                    Analyzing {technicalIndicators.analyzed_pairs} of {technicalIndicators.total_pairs} pairs
                                  </CardDescription>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => refetchIndicators()}
                                >
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Refresh
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-3 gap-4">
                                <div className="text-center">
                                  <p className="text-sm text-muted-foreground">Configured Pairs</p>
                                  <p className="text-xl font-bold">{technicalIndicators.configured_pairs.length}</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-sm text-muted-foreground">Analyzed</p>
                                  <p className="text-xl font-bold">{technicalIndicators.analyzed_pairs}</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-sm text-muted-foreground">Last Update</p>
                                  <p className="text-xl font-bold">
                                    {new Date(technicalIndicators.timestamp).toLocaleTimeString()}
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Indicators by Pair */}
                          {Object.entries(technicalIndicators.indicators).map(([symbol, data]) => (
                            <Card key={symbol}>
                              <CardHeader>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <CardTitle>{symbol}</CardTitle>
                                    <Badge 
                                      variant={
                                        data.overall_signal.color === 'green' ? 'success' :
                                        data.overall_signal.color === 'red' ? 'destructive' :
                                        'secondary'
                                      }
                                    >
                                      {data.overall_signal.signal}
                                    </Badge>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-medium">
                                      ${data.technical_data.price?.toFixed(2) || 'N/A'}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {data.overall_signal.valid_percentage.toFixed(1)}% Favorable
                                    </p>
                                  </div>
                                </div>
                                <CardDescription>{data.overall_signal.message}</CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                  {Object.entries(data.validations).map(([key, validation]) => (
                                    <div
                                      key={key}
                                      className="p-3 bg-muted/50 rounded-lg space-y-2"
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">{validation.name}</span>
                                        {validation.is_valid_for_trading ? (
                                          <CheckCircle className="h-4 w-4 text-green-500" />
                                        ) : (
                                          <XCircle className="h-4 w-4 text-red-500" />
                                        )}
                                      </div>
                                      <p className="text-lg font-bold">
                                        {validation.value !== null ? formatNumber(validation.value) : 'N/A'}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {validation.status.replace('_', ' ')}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </motion.div>
                      ) : (
                        <Card>
                          <CardContent className="flex flex-col items-center justify-center py-16">
                            <Activity className="h-12 w-12 text-muted-foreground mb-4" />
                            <p className="text-muted-foreground mb-4">
                              {detailedInfo?.runtime_status?.is_running 
                                ? 'Technical indicators not available'
                                : 'Bot must be running to view indicators'}
                            </p>
                            {detailedInfo?.runtime_status?.is_running && (
                              <Button onClick={() => refetchIndicators()}>
                                Load Indicators
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* News Tab - Only render if news fetcher is enabled */}
                    {bot.config.news_fetcher && (
                      <TabsContent value="news" className="space-y-6 data-[state=inactive]:hidden">
                      {newsLoading ? (
                        <div className="space-y-4">
                          <Skeleton className="h-32" />
                          <Skeleton className="h-48" />
                          <Skeleton className="h-48" />
                        </div>
                      ) : newsData ? (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          {/* News Summary */}
                          <Card>
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <div>
                                  <CardTitle>News Feed</CardTitle>
                                  <CardDescription>
                                    {newsData.total_articles} articles for {newsData.trading_pairs.join(', ')}
                                  </CardDescription>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => refetchNews()}
                                >
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Refresh
                                </Button>
                              </div>
                            </CardHeader>
                            {newsData.error && (
                              <CardContent>
                                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                                  <p className="text-sm text-destructive">{newsData.error}</p>
                                </div>
                              </CardContent>
                            )}
                          </Card>

                          {/* News Articles */}
                          {newsData.articles && newsData.articles.length > 0 && (
                            <div className="space-y-4">
                              {newsData.articles.map((article) => (
                                <motion.div
                                  key={article.id}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  whileHover={{ x: 4 }}
                                  className="group"
                                >
                                  <Card className="hover:shadow-lg transition-shadow">
                                    <CardContent className="p-4">
                                      <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                          <a
                                            href={getValidUrl(article)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group-hover:text-primary transition-colors"
                                          >
                                            <h4 className="font-medium line-clamp-2 mb-2">
                                              {article.title}
                                            </h4>
                                          </a>
                                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                            <span>{article.domain}</span>
                                            <span>•</span>
                                            <span>{new Date(article.created_at).toLocaleString()}</span>
                                            {article.kind && (
                                              <>
                                                <span>•</span>
                                                <Badge variant="outline" className="text-xs">
                                                  {article.kind}
                                                </Badge>
                                              </>
                                            )}
                                          </div>
                                          {article.currencies && article.currencies.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                              {article.currencies.map((currency, index) => (
                                                <Badge
                                                  key={index}
                                                  variant={
                                                    newsData.crypto_symbols.includes(currency.code.toUpperCase())
                                                      ? "default"
                                                      : "outline"
                                                  }
                                                  className="text-xs"
                                                >
                                                  {currency.code}
                                                </Badge>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                      </div>
                                    </CardContent>
                                  </Card>
                                </motion.div>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      ) : (
                        <Card>
                          <CardContent className="flex flex-col items-center justify-center py-16">
                            <Newspaper className="h-12 w-12 text-muted-foreground mb-4" />
                            <p className="text-muted-foreground mb-4">News not available</p>
                            <Button onClick={() => refetchNews()}>
                              Load News
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>
                    )}

                    {/* LLM Decisions Tab */}
                    <TabsContent value="llm" className="space-y-6 data-[state=inactive]:hidden">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {/* Show read-only notice if LLM is in no-action mode */}
                        {bot.config.llm_no_action && (
                          <Card className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950">
                            <CardContent className="pt-6">
                              <div className="flex items-center gap-3">
                                <Info className="h-5 w-5 text-orange-600" />
                                <div>
                                  <p className="font-medium text-orange-900 dark:text-orange-100">
                                    Read-Only Mode
                                  </p>
                                  <p className="text-sm text-orange-700 dark:text-orange-300">
                                    AI is in analysis mode only. No trading actions will be executed based on LLM decisions.
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Current Auto-Configuration */}
                        {detailedInfo?.current_auto_config && (
                          <Card className="border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-950/50">
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Settings className="h-4 w-4 text-purple-600" />
                                  <CardTitle className="text-base">Current Auto-Configuration</CardTitle>
                                </div>
                                {detailedInfo.current_auto_config.last_updated && (
                                  <Badge variant="outline" className="text-xs">
                                    Updated: {new Date(detailedInfo.current_auto_config.last_updated).toLocaleString()}
                                  </Badge>
                                )}
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {/* Risk Config Summary */}
                                {detailedInfo.current_auto_config.risk_config && (
                                  <div className="space-y-1">
                                    <p className="text-xs font-medium text-muted-foreground">Risk Analysis</p>
                                    <div className="text-xs space-y-1">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">RSI High:</span>
                                        <span className="font-medium">{detailedInfo.current_auto_config.risk_config.extreme_rsi_high}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Volatility:</span>
                                        <span className="font-medium">{detailedInfo.current_auto_config.risk_config.high_volatility}%</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Risk/Trade:</span>
                                        <span className="font-medium">{(detailedInfo.current_auto_config.risk_config.max_risk_per_trade * 100).toFixed(1)}%</span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Trend Config Summary */}
                                {detailedInfo.current_auto_config.trend_config && (
                                  <div className="space-y-1">
                                    <p className="text-xs font-medium text-muted-foreground">Trend Analysis</p>
                                    <div className="text-xs space-y-1">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">ADX Threshold:</span>
                                        <span className="font-medium">{detailedInfo.current_auto_config.trend_config.trend_threshold}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Volume:</span>
                                        <span className="font-medium">{detailedInfo.current_auto_config.trend_config.volume_threshold}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Confidence:</span>
                                        <span className="font-medium">{detailedInfo.current_auto_config.trend_config.overall_confidence_threshold}</span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Volume Config Summary */}
                                {detailedInfo.current_auto_config.volume_config && (
                                  <div className="space-y-1">
                                    <p className="text-xs font-medium text-muted-foreground">Volume Analysis</p>
                                    <div className="text-xs space-y-1">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Min Ratio:</span>
                                        <span className="font-medium">{detailedInfo.current_auto_config.volume_config.min_volume_ratio}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Surge:</span>
                                        <span className="font-medium">{detailedInfo.current_auto_config.volume_config.volume_surge_threshold}x</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Abnormal:</span>
                                        <Badge variant={detailedInfo.current_auto_config.volume_config.abnormal_volume_detection ? "success" : "secondary"} className="h-4 text-xs">
                                          {detailedInfo.current_auto_config.volume_config.abnormal_volume_detection ? 'On' : 'Off'}
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* Loading State */}
                        {decisionsLoading ? (
                          <div className="space-y-4">
                            <Skeleton className="h-10 w-full" />
                            <Skeleton className="h-32 w-full" />
                            <Skeleton className="h-32 w-full" />
                          </div>
                        ) : allDecisions.length > 0 || decisionStats.total > 0 ? (
                          <div className="space-y-4">
                            {/* Header with Stats and Filter */}
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div>
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                  <BrainCircuit className="h-5 w-5" />
                                  AI Decision History
                                </h3>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <Badge variant="outline" className="text-xs">
                                    <Zap className="h-3 w-3 mr-1" />
                                    Trading: {decisionStats.initial}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    <Eye className="h-3 w-3 mr-1" />
                                    Monitoring: {decisionStats.position_monitoring}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    <Shield className="h-3 w-3 mr-1" />
                                    Risk: {decisionStats.risk_management}
                                  </Badge>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8">
                                      <Filter className="h-3 w-3 mr-2" />
                                      {decisionTypeFilter === 'all' ? 'All Types' :
                                       decisionTypeFilter === 'initial' ? 'Trading' :
                                       decisionTypeFilter === 'position_monitoring' ? 'Monitoring' : 'Risk'}
                                      <ChevronDown className="h-3 w-3 ml-2" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => setDecisionTypeFilter('all')}>
                                      All Types ({decisionStats.total})
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setDecisionTypeFilter('initial')}>
                                      <Zap className="h-3 w-3 mr-2" />
                                      Trading Decisions ({decisionStats.initial})
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setDecisionTypeFilter('position_monitoring')}>
                                      <Eye className="h-3 w-3 mr-2" />
                                      Position Monitoring ({decisionStats.position_monitoring})
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setDecisionTypeFilter('risk_management')}>
                                      <Shield className="h-3 w-3 mr-2" />
                                      Risk Management ({decisionStats.risk_management})
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <Badge variant="secondary" className="text-xs">
                                  {allDecisions.length} of {totalDecisions}
                                </Badge>
                              </div>
                            </div>

                            {/* Decision List */}
                            {allDecisions.length > 0 ? (
                              <>
                                {allDecisions.map((decision, index) => (
                                  <React.Fragment key={decision.id}>
                                    {decision.decision_type === 'risk_management' ? (
                                      <RiskManagementCard decision={decision} isLatest={index === 0 && decisionTypeFilter === 'all'} />
                                    ) : decision.decision_type === 'position_monitoring' ? (
                                      <PositionMonitoringCard decision={decision} isLatest={index === 0 && decisionTypeFilter === 'all'} />
                                    ) : (
                                      <TradingDecisionCard decision={decision} isLatest={index === 0 && decisionTypeFilter === 'all'} />
                                    )}
                                  </React.Fragment>
                                ))}

                                {/* Load More Button */}
                                {hasMoreDecisions && (
                                  <div className="flex justify-center pt-4">
                                    <Button
                                      variant="outline"
                                      onClick={() => fetchNextDecisions()}
                                      disabled={isFetchingMoreDecisions}
                                      className="w-full max-w-xs"
                                    >
                                      {isFetchingMoreDecisions ? (
                                        <>
                                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                          Loading...
                                        </>
                                      ) : (
                                        <>
                                          Load More ({totalDecisions - allDecisions.length} remaining)
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </>
                            ) : (
                              <Card>
                                <CardContent className="flex flex-col items-center justify-center py-8">
                                  <Filter className="h-8 w-8 text-muted-foreground mb-3" />
                                  <p className="text-muted-foreground">No decisions match the selected filter</p>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="mt-2"
                                    onClick={() => setDecisionTypeFilter('all')}
                                  >
                                    Show all decisions
                                  </Button>
                                </CardContent>
                              </Card>
                            )}
                          </div>
                        ) : (
                          <Card>
                            <CardContent className="flex flex-col items-center justify-center py-16">
                              <BrainCircuit className="h-12 w-12 text-muted-foreground mb-4" />
                              <p className="text-muted-foreground">No AI decisions recorded yet</p>
                              <p className="text-sm text-muted-foreground mt-2">
                                Start the bot to see LLM analysis and trading decisions
                              </p>
                            </CardContent>
                          </Card>
                        )}
                      </motion.div>
                    </TabsContent>

                    {/* Risk Analysis Tab */}
                    <TabsContent value="risk" className="space-y-6 data-[state=inactive]:hidden">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {/* Analysis Configuration Status */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Settings className="h-5 w-5" />
                              Analysis Configuration
                            </CardTitle>
                            <CardDescription>
                              Current filtering settings for trading decisions
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className={`p-3 rounded-lg border ${
                                bot.analysis_settings?.enable_risk_analysis ?? true
                                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                  : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                              }`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <Shield className={`h-4 w-4 ${
                                    bot.analysis_settings?.enable_risk_analysis ?? true
                                      ? 'text-green-600'
                                      : 'text-gray-500'
                                  }`} />
                                  <span className="font-medium text-sm">Risk Analysis</span>
                                </div>
                                <Badge variant={bot.analysis_settings?.enable_risk_analysis ?? true ? 'success' : 'secondary'}>
                                  {bot.analysis_settings?.enable_risk_analysis ?? true ? 'ENABLED' : 'DISABLED'}
                                </Badge>
                                <p className="text-xs text-muted-foreground mt-2">
                                  {bot.analysis_settings?.enable_risk_analysis ?? true
                                    ? 'Filtering high-risk pairs'
                                    : 'All pairs pass risk check'}
                                </p>
                              </div>

                              <div className={`p-3 rounded-lg border ${
                                bot.analysis_settings?.enable_trend_analysis ?? true
                                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                  : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                              }`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <TrendingUp className={`h-4 w-4 ${
                                    bot.analysis_settings?.enable_trend_analysis ?? true
                                      ? 'text-green-600'
                                      : 'text-gray-500'
                                  }`} />
                                  <span className="font-medium text-sm">Trend Analysis</span>
                                </div>
                                <Badge variant={bot.analysis_settings?.enable_trend_analysis ?? true ? 'success' : 'secondary'}>
                                  {bot.analysis_settings?.enable_trend_analysis ?? true ? 'ENABLED' : 'DISABLED'}
                                </Badge>
                                <p className="text-xs text-muted-foreground mt-2">
                                  {bot.analysis_settings?.enable_trend_analysis ?? true
                                    ? 'Validating trend strength'
                                    : 'Trend validation bypassed'}
                                </p>
                              </div>

                              <div className={`p-3 rounded-lg border ${
                                bot.analysis_settings?.enable_volume_analysis ?? true
                                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                  : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                              }`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <Activity className={`h-4 w-4 ${
                                    bot.analysis_settings?.enable_volume_analysis ?? true
                                      ? 'text-green-600'
                                      : 'text-gray-500'
                                  }`} />
                                  <span className="font-medium text-sm">Volume Analysis</span>
                                </div>
                                <Badge variant={bot.analysis_settings?.enable_volume_analysis ?? true ? 'success' : 'secondary'}>
                                  {bot.analysis_settings?.enable_volume_analysis ?? true ? 'ENABLED' : 'DISABLED'}
                                </Badge>
                                <p className="text-xs text-muted-foreground mt-2">
                                  {bot.analysis_settings?.enable_volume_analysis ?? true
                                    ? 'Confirming with volume'
                                    : 'Volume check bypassed'}
                                </p>
                              </div>
                            </div>

                            {/* Analysis Flow */}
                            <div className="mt-4 p-3 bg-muted/50 border border-border rounded-lg">
                              <h6 className="text-xs font-medium text-foreground mb-2">Current Trading Decision Flow:</h6>
                              <div className="flex items-center text-xs text-muted-foreground space-x-2 overflow-x-auto">
                                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded text-blue-700 dark:text-blue-300 whitespace-nowrap">Technical Indicators</span>
                                <span>→</span>
                                <span className={`px-2 py-1 rounded whitespace-nowrap ${
                                  bot.analysis_settings?.enable_risk_analysis ?? true
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 line-through'
                                }`}>Risk Filter</span>
                                <span>→</span>
                                <span className={`px-2 py-1 rounded whitespace-nowrap ${
                                  bot.analysis_settings?.enable_trend_analysis ?? true
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 line-through'
                                }`}>Trend Filter</span>
                                <span>→</span>
                                <span className={`px-2 py-1 rounded whitespace-nowrap ${
                                  bot.analysis_settings?.enable_volume_analysis ?? true
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 line-through'
                                }`}>Volume Filter</span>
                                <span>→</span>
                                <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 rounded text-purple-700 dark:text-purple-300 whitespace-nowrap">LLM Decision</span>
                              </div>
                            </div>

                            {/* Warning for disabled filters */}
                            {!(bot.analysis_settings?.enable_risk_analysis ?? true) && 
                             !(bot.analysis_settings?.enable_trend_analysis ?? true) && 
                             !(bot.analysis_settings?.enable_volume_analysis ?? true) && (
                              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                      All Analysis Filters Disabled
                                    </p>
                                    <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                      All trading pairs with technical indicators will be sent to the LLM for analysis.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                        {detailedInfo?.risk_analysis ? (
                          <div className="space-y-4">
                            <Card>
                              <CardHeader>
                                <CardTitle>Risk Summary</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                  <div className="text-center">
                                    <p className="text-sm text-muted-foreground">Valid Opportunities</p>
                                    <p className="text-2xl font-bold text-green-600">
                                      {detailedInfo.risk_analysis.valid_opportunities_count}
                                    </p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-sm text-muted-foreground">Risk Filtered</p>
                                    <p className="text-2xl font-bold text-red-600">
                                      {Object.keys(detailedInfo.risk_analysis.detailed_exclusions?.risk_management || {}).length}
                                    </p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-sm text-muted-foreground">Trend Filtered</p>
                                    <p className="text-2xl font-bold text-orange-600">
                                      {Object.keys(detailedInfo.risk_analysis.detailed_exclusions?.trend_analysis || {}).length}
                                    </p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-sm text-muted-foreground">Volume Filtered</p>
                                    <p className="text-2xl font-bold text-blue-600">
                                      {Object.keys(detailedInfo.risk_analysis.detailed_exclusions?.volume_analysis || {}).length}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Risk Management Exclusions */}
                            {detailedInfo.risk_analysis.detailed_exclusions?.risk_management && 
                             Object.keys(detailedInfo.risk_analysis.detailed_exclusions.risk_management).length > 0 && (
                              <Card className="border-red-200 bg-red-50/50 dark:bg-red-900/10">
                                <CardHeader>
                                  <CardTitle className="flex items-center gap-2">
                                    <XCircle className="h-5 w-5 text-red-600" />
                                    Risk Management Exclusions
                                  </CardTitle>
                                  <CardDescription>
                                    Pairs excluded due to extreme risk indicators
                                  </CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-4">
                                    {Object.entries(detailedInfo.risk_analysis.detailed_exclusions.risk_management).map(([symbol, factors]) => (
                                      <div key={symbol} className="p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                                        <div className="flex items-center gap-2 mb-2">
                                          <Badge variant="destructive">{symbol}</Badge>
                                          <span className="text-xs text-muted-foreground">
                                            {Array.isArray(factors) ? factors.length : 0} risk factors
                                          </span>
                                        </div>
                                        <ul className="space-y-1">
                                          {Array.isArray(factors) && factors.map((factor, index) => (
                                            <li key={index} className="flex items-start gap-2 text-xs">
                                              <AlertCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                                              <span>{factor}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ))}
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                            {/* Trend Analysis Exclusions */}
                            {detailedInfo.risk_analysis.detailed_exclusions?.trend_analysis && 
                             Object.keys(detailedInfo.risk_analysis.detailed_exclusions.trend_analysis).length > 0 && (
                              <Card className="border-orange-200 bg-orange-50/50 dark:bg-orange-900/10">
                                <CardHeader>
                                  <CardTitle className="flex items-center gap-2">
                                    <TrendingUp className="h-5 w-5 text-orange-600" />
                                    Trend Analysis Exclusions
                                  </CardTitle>
                                  <CardDescription>
                                    Pairs excluded due to unfavorable trend conditions
                                  </CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-4">
                                    {Object.entries(detailedInfo.risk_analysis.detailed_exclusions.trend_analysis).map(([symbol, reasons]) => (
                                      <div key={symbol} className="p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                                        <div className="flex items-center gap-2 mb-2">
                                          <Badge variant="outline" className="border-orange-500 text-orange-600">
                                            {symbol}
                                          </Badge>
                                          <span className="text-xs text-muted-foreground">
                                            {Array.isArray(reasons) ? reasons.length : 0} trend issues
                                          </span>
                                        </div>
                                        <ul className="space-y-1">
                                          {Array.isArray(reasons) && reasons.map((reason, index) => (
                                            <li key={index} className="flex items-start gap-2 text-xs">
                                              <Info className="h-3 w-3 text-orange-500 mt-0.5 flex-shrink-0" />
                                              <span>{reason}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ))}
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                            {/* Volume Analysis Exclusions */}
                            {detailedInfo.risk_analysis.detailed_exclusions?.volume_analysis && 
                             Object.keys(detailedInfo.risk_analysis.detailed_exclusions.volume_analysis).length > 0 && (
                              <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-900/10">
                                <CardHeader>
                                  <CardTitle className="flex items-center gap-2">
                                    <Activity className="h-5 w-5 text-blue-600" />
                                    Volume Analysis Exclusions
                                  </CardTitle>
                                  <CardDescription>
                                    Pairs excluded due to insufficient volume confirmation
                                  </CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-4">
                                    {Object.entries(detailedInfo.risk_analysis.detailed_exclusions.volume_analysis).map(([symbol, reasons]) => (
                                      <div key={symbol} className="p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                                        <div className="flex items-center gap-2 mb-2">
                                          <Badge variant="outline" className="border-blue-500 text-blue-600">
                                            {symbol}
                                          </Badge>
                                          <span className="text-xs text-muted-foreground">
                                            {Array.isArray(reasons) ? reasons.length : 0} volume issues
                                          </span>
                                        </div>
                                        <ul className="space-y-1">
                                          {Array.isArray(reasons) && reasons.map((reason, index) => (
                                            <li key={index} className="flex items-start gap-2 text-xs">
                                              <Activity className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                                              <span>{reason}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ))}
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                            {/* Legacy support for simple excluded pairs */}
                            {detailedInfo.risk_analysis.excluded_pairs && 
                             detailedInfo.risk_analysis.excluded_pairs.length > 0 && 
                             !detailedInfo.risk_analysis.detailed_exclusions && (
                              <Card className="border-red-200 bg-red-50/50 dark:bg-red-900/10">
                                <CardHeader>
                                  <CardTitle className="flex items-center gap-2">
                                    <XCircle className="h-5 w-5 text-red-600" />
                                    Excluded Trading Pairs
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="flex flex-wrap gap-2">
                                    {detailedInfo.risk_analysis.excluded_pairs.map((pair, index) => (
                                      <Badge key={index} variant="destructive">
                                        {pair}
                                      </Badge>
                                    ))}
                                  </div>
                                </CardContent>
                              </Card>
                            )}

                          </div>
                        ) : (
                          <Card>
                            <CardContent className="flex flex-col items-center justify-center py-16">
                              <Shield className="h-12 w-12 text-muted-foreground mb-4" />
                              {detailedInfo?.runtime_status.is_running ? (
                                <div className="text-center">
                                  <p className="text-muted-foreground mb-2">No risk analysis available yet</p>
                                  <p className="text-sm text-muted-foreground">Bot is running, risk analysis will appear shortly</p>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <p className="text-muted-foreground mb-2">Risk analysis unavailable</p>
                                  <p className="text-sm text-muted-foreground">
                                    Start the bot to see risk analysis and trading pair evaluations
                                  </p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </motion.div>
                    </TabsContent>

                    {/* Activity Logs Tab */}
                    <TabsContent value="logs" className="space-y-6 data-[state=inactive]:hidden">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {/* Log Statistics */}
                        <Card className="mb-4">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-base flex items-center gap-2">
                                <FileText className="h-5 w-5" />
                                Log Statistics
                              </CardTitle>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => refetchLogs()}
                                disabled={logsLoading}
                              >
                                <RefreshCw className={cn("h-3 w-3 mr-2", logsLoading && "animate-spin")} />
                                Refresh
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                              <div className="text-center p-2 sm:p-0">
                                <div className="text-lg sm:text-2xl font-bold">{logStats.total_logs}</div>
                                <div className="text-xs text-muted-foreground">Total Logs</div>
                              </div>
                              <div className="text-center p-2 sm:p-0">
                                <div className="text-lg sm:text-2xl font-bold text-red-500">{logStats.error_count}</div>
                                <div className="text-xs text-muted-foreground">Errors</div>
                              </div>
                              <div className="text-center p-2 sm:p-0">
                                <div className="text-lg sm:text-2xl font-bold text-yellow-500">{logStats.warning_count}</div>
                                <div className="text-xs text-muted-foreground">Warnings</div>
                              </div>
                              <div className="text-center p-2 sm:p-0">
                                <div className="text-lg sm:text-2xl font-bold text-blue-500">{logStats.info_count}</div>
                                <div className="text-xs text-muted-foreground">Info</div>
                              </div>
                              <div className="text-center p-2 sm:p-0 col-span-2 sm:col-span-1">
                                <div className="text-lg sm:text-2xl font-bold text-gray-500">{logStats.debug_count}</div>
                                <div className="text-xs text-muted-foreground">Debug</div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Log Filter and List */}
                        <Card>
                          <CardHeader>
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div>
                                <CardTitle>Activity Logs</CardTitle>
                                <CardDescription>
                                  {allLogs.length} of {totalLogs} logs loaded
                                </CardDescription>
                              </div>
                              <div className="flex items-center gap-2">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8">
                                      <Filter className="h-3 w-3 mr-2" />
                                      {logLevelFilter === 'all' ? 'All Levels' : logLevelFilter}
                                      <ChevronDown className="h-3 w-3 ml-2" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => setLogLevelFilter('all')}>
                                      All Levels
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setLogLevelFilter('ERROR')}>
                                      <span className="text-red-500 mr-2">●</span>
                                      Errors ({logStats.error_count})
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setLogLevelFilter('WARNING')}>
                                      <span className="text-yellow-500 mr-2">●</span>
                                      Warnings ({logStats.warning_count})
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setLogLevelFilter('INFO')}>
                                      <span className="text-blue-500 mr-2">●</span>
                                      Info ({logStats.info_count})
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setLogLevelFilter('DEBUG')}>
                                      <span className="text-gray-500 mr-2">●</span>
                                      Debug ({logStats.debug_count})
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8"
                                  onClick={handleDownloadLogs}
                                  disabled={isDownloadingLogs || totalLogs === 0}
                                >
                                  {isDownloadingLogs ? (
                                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                                  ) : (
                                    <Download className="h-3 w-3 mr-2" />
                                  )}
                                  Download
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {logsLoading ? (
                              <div className="space-y-2">
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-16 w-full" />
                              </div>
                            ) : allLogs.length > 0 ? (
                              <div className="space-y-2">
                                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                  {allLogs.map((log, index) => (
                                    <div
                                      key={`${log.timestamp}-${index}`}
                                      className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg text-sm hover:bg-muted/70 transition-colors"
                                    >
                                      <Badge
                                        variant={
                                          log.level === 'ERROR' ? 'destructive' :
                                          log.level === 'WARNING' ? 'warning' :
                                          log.level === 'INFO' ? 'info' :
                                          log.level === 'DEBUG' ? 'secondary' :
                                          'default'
                                        }
                                        className="mt-0.5 min-w-[60px] justify-center"
                                      >
                                        {log.level}
                                      </Badge>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                          <p className="break-words font-medium">{log.message}</p>
                                          {log.logger_name && (
                                            <Badge variant="outline" className="text-xs shrink-0">
                                              {log.logger_name}
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-4 mt-1">
                                          <p className="text-xs text-muted-foreground">
                                            {new Date(log.timestamp).toLocaleString()}
                                          </p>
                                          {log.module && (
                                            <span className="text-xs text-muted-foreground">
                                              {log.module}{log.function ? `.${log.function}` : ''}{log.line ? `:${log.line}` : ''}
                                            </span>
                                          )}
                                        </div>
                                        {log.exception && (
                                          <div className="mt-2">
                                            <Badge variant="destructive" className="text-xs mb-1">Exception</Badge>
                                            <pre className="mt-1 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs overflow-x-auto">
                                              {log.exception}
                                            </pre>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Load More Button */}
                                {hasMoreLogs && (
                                  <div className="flex justify-center pt-4">
                                    <Button
                                      variant="outline"
                                      onClick={() => fetchNextLogs()}
                                      disabled={isFetchingMoreLogs}
                                      className="w-full max-w-xs"
                                    >
                                      {isFetchingMoreLogs ? (
                                        <>
                                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                          Loading...
                                        </>
                                      ) : (
                                        <>
                                          Load More ({totalLogs - allLogs.length} remaining)
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="p-3 bg-muted rounded-full mb-4">
                                  <FileText className="h-8 w-8 text-muted-foreground" />
                                </div>
                                <p className="font-medium text-muted-foreground">No Logs Available</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {logLevelFilter !== 'all' ? (
                                    <>
                                      No {logLevelFilter} logs found.{' '}
                                      <Button
                                        variant="link"
                                        className="p-0 h-auto"
                                        onClick={() => setLogLevelFilter('all')}
                                      >
                                        Show all levels
                                      </Button>
                                    </>
                                  ) : (
                                    'Logs will appear here when the bot starts running'
                                  )}
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    </TabsContent>

                    {/* Configuration Tab */}
                    <TabsContent value="config" className="space-y-6 data-[state=inactive]:hidden">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {/* Analysis Settings */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Shield className="h-5 w-5" />
                              Analysis Settings
                            </CardTitle>
                            <CardDescription>Trading analysis pipeline configuration</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-3 bg-muted/50 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium flex items-center gap-1.5">
                                      <Shield className="h-3.5 w-3.5" />
                                      Risk Analysis
                                    </span>
                                    <Badge variant={bot.analysis_settings?.enable_risk_analysis ?? true ? 'success' : 'secondary'}>
                                      {bot.analysis_settings?.enable_risk_analysis ?? true ? 'ON' : 'OFF'}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Filters high-risk pairs based on volatility and risk indicators
                                  </p>
                                </div>

                                <div className="p-3 bg-muted/50 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium flex items-center gap-1.5">
                                      <TrendingUp className="h-3.5 w-3.5" />
                                      Trend Analysis
                                    </span>
                                    <Badge variant={bot.analysis_settings?.enable_trend_analysis ?? true ? 'success' : 'secondary'}>
                                      {bot.analysis_settings?.enable_trend_analysis ?? true ? 'ON' : 'OFF'}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Validates trend strength using ADX and momentum indicators
                                  </p>
                                </div>

                                <div className="p-3 bg-muted/50 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium flex items-center gap-1.5">
                                      <BarChart3 className="h-3.5 w-3.5" />
                                      Volume Analysis
                                    </span>
                                    <Badge variant={bot.analysis_settings?.enable_volume_analysis ?? true ? 'success' : 'secondary'}>
                                      {bot.analysis_settings?.enable_volume_analysis ?? true ? 'ON' : 'OFF'}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Confirms signals with volume patterns and price-volume correlation
                                  </p>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Bot Configuration */}
                        <Card>
                          <CardHeader>
                            <CardTitle>Bot Configuration</CardTitle>
                            <CardDescription>Current bot settings and API configuration</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              {/* Basic Settings */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center p-3 bg-muted/50 rounded-lg">
                                  <p className="text-sm text-muted-foreground">Trading Capital</p>
                                  <p className="text-lg font-bold">{formatCurrency(bot.config.trading_capital)}</p>
                                </div>
                                <div className="text-center p-3 bg-muted/50 rounded-lg">
                                  <p className="text-sm text-muted-foreground">Max Risk</p>
                                  <p className="text-lg font-bold">{formatPercentage(bot.config.max_risk_per_trade)}</p>
                                </div>
                                <div className="text-center p-3 bg-muted/50 rounded-lg">
                                  <p className="text-sm text-muted-foreground">Environment</p>
                                  <p className="text-lg font-bold">{bot.config.testnet ? 'Testnet' : 'Live'}</p>
                                </div>
                                <div className="text-center p-3 bg-muted/50 rounded-lg">
                                  <p className="text-sm text-muted-foreground">Trading Pairs</p>
                                  <p className="text-lg font-bold">{bot.config.coins.length}</p>
                                </div>
                              </div>

                              {/* Trading Pairs */}
                              <div>
                                <h4 className="font-medium mb-2">Trading Pairs</h4>
                                <div className="flex flex-wrap gap-1">
                                  {bot.config.coins.map((coin, index) => (
                                    <Badge key={index} variant="outline">{coin}</Badge>
                                  ))}
                                </div>
                              </div>

                              {/* Features */}
                              <div>
                                <h4 className="font-medium mb-2">Enabled Features</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  <div className="flex items-center gap-2">
                                    <Badge variant={bot.config.news_fetcher ? 'success' : 'secondary'}>
                                      {bot.config.news_fetcher ? '✓' : '✗'}
                                    </Badge>
                                    <span className="text-sm">News Fetching</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={bot.config.llm_no_action ? 'warning' : 'success'}>
                                      {bot.config.llm_no_action ? '✓' : '✗'}
                                    </Badge>
                                    <span className="text-sm">Safe Mode</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={bot.notification_settings?.enabled ? 'success' : 'secondary'}>
                                      {bot.notification_settings?.enabled ? '✓' : '✗'}
                                    </Badge>
                                    <span className="text-sm">Notifications</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={bot.config.testnet ? 'warning' : 'success'}>
                                      {bot.config.testnet ? '✓' : '✗'}
                                    </Badge>
                                    <span className="text-sm">Testnet</span>
                                  </div>
                                </div>
                              </div>

                              {/* Technical Configuration (collapsed by default) */}
                              <details className="group">
                                <summary className="cursor-pointer font-medium py-2 flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                                  Technical Configuration (JSON)
                                </summary>
                                <div className="mt-2">
                                  <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs">
                                    {JSON.stringify({
                                      config: bot.config,
                                      analysis_settings: bot.analysis_settings,
                                      notification_settings: bot.notification_settings
                                    }, null, 2)}
                                  </pre>
                                </div>
                              </details>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </TabsContent>
                  </>
                )}
              </div>
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BotDetailModal;